import { createFileRoute, ClientOnly, Link } from "@tanstack/react-router";
import { useAccount, useBalance } from "wagmi";
import { useQuery } from "@tanstack/react-query";
import { openAppKit } from "@/lib/wallet";
import { formatEther } from "viem";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { Wallet, Layers, Sparkles, ShoppingCart, Link2, Coins } from "lucide-react";

export const Route = createFileRoute("/dashboard")({
  component: () => (<ClientOnly fallback={null}><DashboardPage /></ClientOnly>),
  head: () => ({
    meta: [
      { title: "Dashboard - RobotDex" },
      { name: "description", content: "Your RobotDex wallet dashboard: balance, chain, minted and created NFTs." },
    ],
  }),
});

function DashboardPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { address, isConnected, chain } = useAccount();
  

  if (!mounted) return <div className="p-8 text-muted-foreground">Loading…</div>;

  if (!isConnected || !address) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <Wallet className="h-10 w-10 mx-auto text-primary" />
        <h1 className="mt-4 text-2xl font-bold neon-text">Connect your wallet</h1>
        <p className="text-muted-foreground mt-2">Connect a wallet to see your balance, drops, and mint history.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button onClick={() => openAppKit()} className="neon-glow">Connect Wallet</Button>
          <Button asChild variant="outline" className="neon-border"><Link to="/market">Browse Market</Link></Button>
        </div>
      </div>
    );
  }

  return <ConnectedDashboard address={address} chain={chain} />;
}

function ConnectedDashboard({ address, chain }: { address: `0x${string}`; chain: any }) {
  const { data: balance } = useBalance({ address });
  const addr = address.toLowerCase();

  const { data: mine } = useQuery({
    queryKey: ["dashboard", addr],
    queryFn: async () => {
      const [minted, created, activity] = await Promise.all([
        supabase.from("activities").select("*", { count: "exact", head: true }).eq("actor_wallet", addr).in("kind", ["mint", "mint_request"]),
        supabase.from("nft_drops").select("*", { count: "exact", head: true }).eq("creator_wallet", addr),
        supabase.from("activities").select("*, nft_drops(name, symbol)").eq("actor_wallet", addr).order("created_at", { ascending: false }).limit(5),
      ]);
      return {
        minted: minted.count ?? 0,
        created: created.count ?? 0,
        recent: activity.data ?? [],
      };
    },
    refetchOnWindowFocus: false,
  });

  const nativeValue = balance ? Number(formatEther(balance.value)).toFixed(4) : "-";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <PageHeader
        title="Dashboard"
        subtitle={`Signed in as ${address.slice(0, 6)}…${address.slice(-4)}`}
        actions={
          <Badge variant="outline" className="neon-border">
            <Link2 className="h-3 w-3 mr-1 text-primary" /> {chain?.name ?? "Unknown chain"}
          </Badge>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Coins}
          label={`Balance (${balance?.symbol ?? chain?.nativeCurrency?.symbol ?? "ETH"})`}
          value={nativeValue}
        />
        <StatCard icon={Link2} label="Connected Chain" value={chain?.name ?? "-"} sub={`ID ${chain?.id ?? "-"}`} />
        <StatCard icon={Sparkles} label="Minted / Bought" value={mine?.minted ?? 0} />
        <StatCard icon={Layers} label="Drops Created" value={mine?.created ?? 0} />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 bg-card/60 neon-border">
          <h3 className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Create a drop</h3>
          <p className="mt-2 text-sm text-muted-foreground">Launch your own NFT collection with mint window and supply limits.</p>
          <Button asChild className="mt-4 w-full neon-glow"><Link to="/create">New drop</Link></Button>
        </Card>
        <Card className="p-5 bg-card/60 neon-border">
          <h3 className="font-semibold flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-primary" /> Browse market</h3>
          <p className="mt-2 text-sm text-muted-foreground">Discover live drops and secondary listings across chains.</p>
          <Button asChild variant="outline" className="mt-4 w-full neon-border"><Link to="/market">Open market</Link></Button>
        </Card>
        <Card className="p-5 bg-card/60 neon-border">
          <h3 className="font-semibold flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Your collections</h3>
          <p className="mt-2 text-sm text-muted-foreground">View drops you've created and manage listings.</p>
          <Button asChild variant="outline" className="mt-4 w-full neon-border"><Link to="/collections">My collections</Link></Button>
        </Card>
      </div>

      <Card className="mt-6 p-5 bg-card/60 neon-border">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Your recent activity</h3>
          <Button asChild variant="ghost" size="sm"><Link to="/activity">View all →</Link></Button>
        </div>
        <div className="mt-3 divide-y divide-border/60">
          {(!mine?.recent || mine.recent.length === 0) && (
            <div className="py-6 text-sm text-muted-foreground text-center">No activity yet.</div>
          )}
          {mine?.recent?.map((a: any) => (
            <div key={a.id} className="py-3 flex items-center gap-3 text-sm">
              <Badge variant="outline" className="neon-border uppercase text-[10px] tracking-wider">{a.kind.replace("_", " ")}</Badge>
              <div className="flex-1 truncate">{a.nft_drops?.name ?? "-"}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }: any) {
  return (
    <Card className="p-5 bg-card/60 neon-border">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-muted-foreground truncate">{label}</div>
          <div className="text-2xl font-bold mt-1 neon-text truncate">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
        </div>
        <Icon className="h-8 w-8 text-primary shrink-0" />
      </div>
    </Card>
  );
}
